import { LngLat, LngLatLike, Map, MapMouseEvent, Marker, Popup } from "mapbox-gl"
import { ShareModalContent } from "./shareModal"

let mapboxMap: Map
let mapsPlugin: meteoblueMapsPlugin
const currentLocationMarker = document.createElement('div')
const meteogramModalContainer = document.createElement('div')

let isMeteogramOpen = false

const initMap = () => {

    const center: LngLatLike = [mb.settings.loc.lon, mb.settings.loc.lat]

    mapboxMap = new mapboxgl.Map({
        center,
        container: 'webmapv2',
        hash: 'coords',
        zoom: 4,
        // disable arrow key left, right, because it conflicts with the map player
        keyboard: false
    })

    mapboxMap.dragRotate.disable()
    mapboxMap.touchZoomRotate.disableRotation()
    // @ts-ignore
    mapboxMap.touchPitch.disable();

    const host = mb.settings.environment !== 'production'
        ? 'https://maps-api-staging.meteoblue.com'
        : 'https://maps-api.meteoblue.com'

    // @todo: set units
    const inventory = host + `/v1/map/inventory/weather-maps?lang=${mb.settings.lang}`
        + '&enableOpenlayersLicensing=true'
        + '&temperatureUnit=' + getUnit(mb.settings.tempunit, 'old')
        + '&velocityUnit=' + getUnit(mb.settings.windunit, 'old')
        + '&lengthUnit=' + getUnit(mb.settings.lengthunit, 'old')

    setCurrentLocationMarker(center)

    const socialShare = () => mapsPlugin.openModal({
        title: mb.translations["Share"],
        closeLabel: mb.translations["Close"],
        content: ShareModalContent()
    })

    mapsPlugin = new meteoblueMapsPlugin({
        mapboxMap,
        inventory,
        controls: {
            downloadStyle: false,
        },
        buffer: false,
        arrowKeys: true,
        showLoadingTime: false,
        apiKey: '33d00c4b0378',
        timezoneOffsetInSeconds: parseInt(mb.settings.utcOffset),
        timezoneAbbreviation: mb.settings.timezone,
        language: mb.settings.lang,
        canUseRestricted: mb.settings.hasPointPlus,
        screenshotLogoPath: '/website/images/webmaps/meteoblue_logo-glow.png',
        restrictedNotice: {
            content: mb.translations["This map requires point+. Time selection has been disabled."],
            actionLabel: mb.translations["About point+"],
            action: () => window.open("https://www.meteoblue.com/pointplus"),
        },
        customMenuEntries: [
            { label: mb.translations["Screenshot"], action: 'screenshot' },
            { label: mb.translations["Share"], action: socialShare },
            { label: mb.translations["Help"], action: mb.settings.mapsHelpLink },
            { label: '©', action: 'attribution' }
        ],
        content: {
            top: document.getElementById('logo-container'),
            main: meteogramModalContainer
        }
    })

    mapboxMap.on('click', (event) => openContextMenu(event, mapboxMap))

    mapboxMap.on('load', () => {
        mapboxMap.resize()
        window.addEventListener('navigationcollapse', () => mapboxMap.resize())
        window.addEventListener('navigationexpand', () => mapboxMap.resize())
    })

    mapsPlugin.on('unitchange', (event) => {
        const name = getUnit(event.unitType, 'new')
        const value = getUnit(event.unit, 'new')
        const date = new Date()
        date.setTime(date.getTime() + (720 * 24 * 60 * 60 * 1000))
        document.cookie = name + "=" + encodeURI(value) + "; path = /; expires=" + date.toUTCString() + ";secure "
    })
}

/**
 * Mapping form old to new units
 * @param unit the unit or unit type which has to be converted
 * @param oldOrNew specifies if the given unit is old or new 
 */
const getUnit = (unit, oldOrNew: string) => {

    // unit mapping, "new": "old"
    const unitMappings = [

        // unit types
        { new: "temperature", old: "temp" },
        { new: "velocity", old: "speed" },
        { new: "length", old: "precip" },
        { new: "energy", old: "energy" },

        // units
        { new: "C", old: "CELSIUS" },
        { new: "F", old: "FAHRENHEIT" },
        { new: "km/h", old: "KILOMETER_PER_HOUR" },
        { new: "bft", old: "BEAUFORT" },
        { new: "m/s", old: "METER_PER_SECOND" },
        { new: "mph", old: "MILE_PER_HOUR" },
        { new: "kn", old: "KNOT" },
        { new: "metric", old: "MILLIMETER" },
        { new: "imperial", old: "INCH" },
    ]

    if (oldOrNew === 'old') {
        const newUnit = unitMappings
            .find((unitMapping) => unit === unitMapping.old)
        return newUnit ? newUnit.new : unit
    } else {
        const oldUnit = unitMappings
            .find((unitMapping) => unit === unitMapping.new)
        return oldUnit ? oldUnit.old : unit
    }
}

const openContextMenu = (event: MapMouseEvent, mapboxMap: mapboxgl.Map) => {

    if (isMeteogramOpen) {
        fetchMeteogram(event.lngLat)
        return
    }

    // hide tooltip. timeout required by mobile chrome.
    setTimeout(() => mapsPlugin.disableTooltip(), 10)

    setTimeout(
        () => mapboxMap.once('mousemove', () => mapsPlugin.enableTooltip()),
        500
    )

    const popup = new Popup({
        closeButton: false, // we use a custom one
        closeOnMove: true,
        anchor: 'top-left',
        className: 'webmap_context_menu'
    })

    const menuContent = document.createElement('div')

    const closeLink = document.createElement('a')
    closeLink.classList.add('close_icon')
    closeLink.onclick = () => {
        popup.remove()
        if(popupElement) { mapsPlugin.enableTooltip() }
    }
    menuContent.appendChild(closeLink)

    const locationNameEl = document.createElement('div')
    locationNameEl.classList.add('location_name', 'loading_animation')
    menuContent.appendChild(locationNameEl)

    const location = fetchClosestLocation(event.lngLat)
    location.then((location) => {
        locationNameEl.innerHTML = location.name
        locationNameEl.title = `${event.lngLat.lat.toFixed(4)} / ${event.lngLat.lng.toFixed(4)}`
        locationNameEl.classList.remove('loading_animation') 
    }).catch((error) => {
        console.error("Could not load location info: " + error)
        locationNameEl.remove()
    })

    const formattedMapValue = mapsPlugin.getFormattedMapValue(event.point)

    if (formattedMapValue !== undefined) {
        const mapValueEl = document.createElement('div')
        mapValueEl.innerHTML = formattedMapValue
        menuContent.appendChild(mapValueEl)
    }
    
    const meteogramLink = document.createElement('a')
    meteogramLink.classList.add('webmap_meteogram_link')
    meteogramLink.innerText = mb.translations['Show meteogram']
    meteogramLink.onclick = () => {
        fetchMeteogram(event.lngLat)
        popup.remove()
        if(popupElement) { mapsPlugin.enableTooltip() }
    }
    menuContent.appendChild(meteogramLink)

    popup
        .setLngLat(event.lngLat)
        .setDOMContent(menuContent)
        .addTo(mapboxMap);

    const popupElement = popup.getElement()

    if(popupElement) {
        popupElement.addEventListener('mouseenter', mapsPlugin.disableTooltip.bind(mapsPlugin))
        popupElement.addEventListener('mouseleave', mapsPlugin.enableTooltip.bind(mapsPlugin))
    }
}

const fetchClosestLocation = (lngLat: LngLat) => {

    const url = "/"
        + window.mb.settings.lang
        + "/weather/webmap/closestlocation/"
        + window.mb.settings.loc.url
        + "?lat=" + lngLat.lat
        + "&lon=" + lngLat.lng

    const promise = fetch(url, { headers: {'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => response.json())
        .then((location) => {
            return location
        })

    return promise
}

const fetchMeteogram = (lngLat: LngLat) => {

    const meteogramModal = MeteogramModal()
    meteogramModal.setLoading(true)

    const meteogramUrl = "/"
        + window.mb.settings.lang
        + "/weather/webmap/meteogram/"
        + window.mb.settings.loc.url
        + "?lat=" + lngLat.lat
        + "&lon=" + lngLat.lng
    
    const meteogramPromise = fetch(meteogramUrl, {headers: {'X-Requested-With': 'XMLHttpRequest'}})
        .then((response) => response.json())
        .then((urls) => {
            return urls
        })

    const locationPromise = fetchClosestLocation(lngLat)

    Promise.all([meteogramPromise, locationPromise])
        .then((result) => {
            const [meteogramUrls, location] = result
            meteogramModal.setSrc(meteogramUrls)
            meteogramModal.setLocation(location)
            meteogramModal.setLoading(false)
            setCurrentLocationMarker(lngLat)
        })
        .catch((error) => {
            meteogramModal.setLoading(false)
            meteogramModal.setError(error)
        })

}

const MeteogramModal = () => {

    isMeteogramOpen = true

    meteogramModalContainer.innerText = ''
    meteogramModalContainer.style.display = 'block'
    meteogramModalContainer.classList.add('webmap_meteogram_modal')

    // loading
    const loadingEl = document.createElement('div')
    loadingEl.classList.add('loading')

    // error
    const errorEl = document.createElement('div')
    errorEl.classList.add('webmap_meteogram_error')

    // wrapper
    const meteogramWrapper = document.createElement('div')
    meteogramWrapper.classList.add('webmap_meteogram_wrapper')
    meteogramModalContainer.appendChild(meteogramWrapper)

    // header
    const headerEl = document.createElement('div')
    headerEl.classList.add('meteogram_header')
    meteogramWrapper.appendChild(headerEl)

    // location name
    const locationNameEl = document.createElement('a')
    headerEl.appendChild(locationNameEl)

    // close
    const closeEl = document.createElement('div')
    closeEl.classList.add('header_close')
    closeEl.onclick = () => {
        meteogramModalContainer.removeEventListener('mouseenter', disableTooltip)
        meteogramModalContainer.removeEventListener('mouseleave', enableTooltip)
        meteogramModalContainer.style.display = 'none'
        isMeteogramOpen = false
    }
    headerEl.appendChild(closeEl)

    // meteogram container
    const meteogramContainer = document.createElement('div')
    meteogramContainer.classList.add('webmap_meteogram_container')
    meteogramWrapper.appendChild(meteogramContainer)

    // meteogram image
    const meteogramImage = document.createElement('img')

    // hide tooltip when entering meteogram container
    const disableTooltip = () => mapsPlugin.disableTooltip()
    const enableTooltip = () => mapsPlugin.enableTooltip()
    meteogramModalContainer.addEventListener('mouseenter', disableTooltip)
    meteogramModalContainer.addEventListener('mouseleave', enableTooltip)

    const setLocation = (location) => {
        locationNameEl.classList.add('location_name')
        locationNameEl.innerText = `${location.name} (${location.local_time})`
        locationNameEl.href = `/${window.mb.settings.lang}/weather/forecast/week/${location.url}`
    }
    
    const setSrc = (urls) => {
        meteogramImage.src = urls.src
        meteogramImage.srcset = urls.srcset
        meteogramContainer.appendChild(meteogramImage)
    }

    const setLoading = (isLoading: boolean) => {
        if (isLoading) {
            meteogramWrapper.appendChild(loadingEl)
        } else {
            meteogramWrapper.removeChild(loadingEl)
        }
    }

    const setError = (errorMsg: string) => {
        errorEl.innerHTML = `Could not load meteogram.<br>${errorMsg}`
        meteogramWrapper.appendChild(errorEl)
    }

    return Object.freeze({
        setLocation,
        setSrc,
        setLoading,
        setError,
    })
}

const setCurrentLocationMarker = (lngLat) => {
    currentLocationMarker.className = 'current_location_marker'
    new Marker(currentLocationMarker)
        .setLngLat(lngLat)
        .addTo(mapboxMap)
}

initMap()
